import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CalendarPlus, Loader2 } from 'lucide-react';
import {
  getHappeningSeriesById,
  previewSeriesExtension,
  extendSeries,
  describeRecurrencePattern,
  type Happening,
} from '@littlebooker/dal';

interface ExtendSeriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seriesId: string;
  /** Called after instances are created, with the new happenings. */
  onExtended?: (created: Happening[]) => void;
}

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

export function ExtendSeriesDialog({
  open,
  onOpenChange,
  seriesId,
  onExtended,
}: ExtendSeriesDialogProps) {
  const queryClient = useQueryClient();
  const [toDate, setToDate] = useState('');

  const seriesQuery = useQuery({
    queryKey: ['happeningSeries', seriesId],
    queryFn: () => getHappeningSeriesById(seriesId),
    enabled: open,
  });
  const series = seriesQuery.data;

  // Seed the picker with the series' current end date so the field is never empty; the preview
  // then correctly reports that it adds nothing until the user moves it forward.
  useEffect(() => {
    if (open && series && !toDate) {
      setToDate(series.recurrence_end_date);
    }
    if (!open) setToDate('');
  }, [open, series, toDate]);

  const previewQuery = useQuery({
    queryKey: ['seriesExtensionPreview', seriesId, toDate],
    queryFn: () => previewSeriesExtension({ seriesId, toDate }),
    enabled: open && !!toDate,
  });

  const extendMutation = useMutation({
    mutationFn: () => extendSeries({ seriesId, toDate }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['happeningSeries', seriesId] });
      queryClient.invalidateQueries({ queryKey: ['seriesExtensionPreview'] });
      onExtended?.(created);
      onOpenChange(false);
    },
  });

  const newDates = previewQuery.data?.dates ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="medium">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5" />
            Add more dates{series ? ` — ${series.name}` : ''}
          </DialogTitle>
          <DialogDescription>
            Continue the series past its current end date. The pattern, start time, duration and
            defaults all carry over, and episode numbering continues from the last episode.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 px-4 sm:px-6 py-4">
          {seriesQuery.isLoading ? (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading the series…
            </div>
          ) : !series ? (
            <p className="py-6 text-sm text-destructive">Could not load this series.</p>
          ) : (
            <>
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                <div className="font-medium">{describeRecurrencePattern(series.rrule)}</div>
                <div className="text-muted-foreground">
                  at {series.default_start_time.slice(0, 5)} · currently runs through{' '}
                  {formatDate(series.recurrence_end_date)}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium">Extend through</label>
                <input
                  type="date"
                  value={toDate}
                  min={series.recurrence_end_date}
                  onChange={(e) => setToDate(e.target.value)}
                  disabled={extendMutation.isPending}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Inclusive — a show falling on this date is created.
                </p>
              </div>

              {previewQuery.isLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Working out the dates…
                </div>
              ) : previewQuery.isError ? (
                <p className="text-sm text-destructive">
                  Could not build a preview: {(previewQuery.error as Error).message}
                </p>
              ) : newDates.length === 0 ? (
                <p className="py-2 text-sm text-muted-foreground">
                  No new dates yet — pick an end date past{' '}
                  {formatDate(series.recurrence_end_date)}.
                </p>
              ) : (
                <>
                  <div className="max-h-56 overflow-y-auto rounded-md border">
                    <ul className="divide-y text-sm">
                      {newDates.map((date) => (
                        <li key={date} className="px-3 py-2">
                          {formatDate(date)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Existing shows are left alone, and dates you removed on purpose are not
                    recreated — generation starts after the series&apos; last existing show.
                  </p>
                </>
              )}

              {extendMutation.isError && (
                <p className="text-sm text-destructive">
                  Could not add the dates: {(extendMutation.error as Error).message}
                </p>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={extendMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => extendMutation.mutate()}
            disabled={newDates.length === 0 || previewQuery.isLoading || extendMutation.isPending}
          >
            {extendMutation.isPending
              ? 'Adding…'
              : `Add ${newDates.length} date${newDates.length !== 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
